import React, { useEffect, useRef, useState } from 'react';
import { api, authStore } from './api';

const events = new EventTarget();

let socket = null;
let reconnectTimer = null;
let pingTimer = null;
let online = 0;

const buildWsUrl = () => {
  const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/^http/, 'ws');
  const token = authStore.token;
  return token ? `${base}/api/ws?token=${encodeURIComponent(token)}` : null;
};

const connect = () => {
  const url = buildWsUrl();
  if (!url) return;
  try {
    socket = new WebSocket(url);
  } catch {
    scheduleReconnect();
    return;
  }
  socket.onopen = () => {
    pingTimer = setInterval(() => {
      try { socket && socket.readyState === 1 && socket.send('ping'); } catch {}
    }, 25000);
  };
  socket.onmessage = (e) => {
    try {
      const data = JSON.parse(e.data);
      if (data.type === 'presence' || data.type === 'hello') {
        online = data.payload?.online ?? online;
      }
      events.dispatchEvent(new CustomEvent('ws', { detail: data }));
    } catch {}
  };
  socket.onclose = () => {
    if (pingTimer) clearInterval(pingTimer);
    pingTimer = null;
    socket = null;
    scheduleReconnect();
  };
  socket.onerror = () => {
    try { socket && socket.close(); } catch {}
  };
};

const scheduleReconnect = () => {
  if (reconnectTimer) return;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    if (authStore.token) connect();
  }, 4000);
};

export const wsClient = {
  start() { if (!socket && authStore.token) connect(); },
  stop() {
    if (pingTimer) clearInterval(pingTimer);
    pingTimer = null;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnectTimer = null;
    try { socket && socket.close(); } catch {}
    socket = null;
  },
  onEvent(handler) {
    const fn = (e) => handler(e.detail);
    events.addEventListener('ws', fn);
    return () => events.removeEventListener('ws', fn);
  },
};

export const usePresence = () => {
  const [count, setCount] = useState(online);
  useEffect(() => {
    const off = wsClient.onEvent((data) => {
      if (data.type === 'presence' || data.type === 'hello') {
        setCount(data.payload?.online ?? 0);
      }
    });
    // Fallback initial fetch
    api.get('/presence').then(({ data }) => setCount(data.online)).catch(() => {});
    return off;
  }, []);
  return count;
};
